import {inject, Injectable, signal} from '@angular/core';
import {FatalSseError, ServerSentEventsService} from './server-sent-events.service';
import {CallStatus, PhoneCallEvent} from '@shared/phone-call-event';
import {Subscription, filter, firstValueFrom, take} from 'rxjs';
import {InitService} from './init.service';
import {User} from '@shared/user';
import {StorageService} from './storage.service';

/*
 * Reihenfolge des Lebenszyklus, um verspätete/doppelte Events derselben starfaceCallId zu erkennen -
 * Starface schickt pro Anruf-Leg ein eigenes Webhook, die Reihenfolge zwischen Legs ist NICHT garantiert
 * (siehe StarfaceController::persistCall() im Backend). Ein Event, das im Vergleich zum zuletzt gesehenen
 * Status desselben Anrufs "zurückspringt" (z.B. RINGING nach CONNECTED, oder CONNECTED nach HANGUP), ist
 * verspätet/veraltet und darf nicht mehr angezeigt werden.
 */
const CALL_STATUS_ORDER: Record<CallStatus, number> = {
    incoming: 0,
    active: 1,
    ended: 2,
    // liegt außerhalb des Lebenszyklus, da "missed" nur im Frontend gesetzt wird und nie per SSE kommt
    missed: -1,
};

@Injectable({
    providedIn: 'root',
})
export class PhoneCallService {
    private sseService = inject(ServerSentEventsService);
    private storageService = inject(StorageService);
    private initService = inject(InitService);

    private subscription: Subscription | null = null;

    public lastCall = signal<PhoneCallEvent | null>(null);

    /*
     * Rohes Event-Signal für Consumer außerhalb des Anruf-Popups (z.B. Badge für verpasste Anrufe).
     * "lastCall" wird vom Popup selbst wieder auf null gesetzt (Dismiss-Logik), daher ungeeignet für
     * andere Consumer, die jedes Event sehen müssen - "lastEvent" wird nur hier in der SSE-Callback gesetzt.
     */
    public lastEvent = signal<PhoneCallEvent | null>(null);

    public async startSseStream() {
        // Bereits laufenden SSE-Stream beenden (z.B. wenn startSseStream() über mehrere Wege beim App-Start ausgelöst wird)
        this.stopSseStream();

        /*
         * Warten, bis die Backend-Config im Storage liegt.
         *
         * Direkt nach dem Login ruft der AuthService initializeAll() auf und startet den
         * Stream unmittelbar danach - die Config (u.a. "config|sse" mit der Hub-URL) wird
         * aber erst asynchron geschrieben und wäre hier noch nicht da.
         *
         * Beim Page-Reload ist die Config schon im Storage und "allInitialized" wird nicht
         * erneut ausgelöst. Deshalb der Kurzschluss über initialized (kommt aus dem
         * localStorage und übersteht den Reload) - sonst würde hier ewig gewartet.
         */
        if (!this.initService.initialized) {
            await firstValueFrom(
                this.initService.allInitialized.pipe(
                    filter((isInitialized) => isInitialized),
                    take(1),
                ),
            );
        }

        const ownUser = await this.storageService.getItem<User | null>('ownUser');
        const employeeId = ownUser?.employee_id ?? 0;

        this.subscription = this.sseService.listen<PhoneCallEvent>(`user/${employeeId}`).subscribe({
            next: (event) => {
                /*
                 * Verspätetes/dupliziertes Event für denselben Anruf (siehe CALL_STATUS_ORDER oben) ignorieren -
                 * Vergleich gegen "lastEvent" (nie von außen zurückgesetzt), NICHT gegen "lastCall", da Letzteres
                 * vom Popup selbst genullt wird (Dismiss) und dadurch einen verspäteten Vergleich unterlaufen würde
                 */
                const previous = this.lastEvent();
                const isStaleEvent =
                    previous?.starfaceCallId === event.starfaceCallId &&
                    CALL_STATUS_ORDER[event.callStatus] < CALL_STATUS_ORDER[previous.callStatus];

                if (isStaleEvent) {
                    return;
                }

                this.lastCall.set(event);
                this.lastEvent.set(event);
            },
            error: (err) => {
                // console.error(err);

                /*
                 * Bei einem fatalen Fehler NICHT neu starten - z.B. fehlende Hub-URL in der
                 * Config oder ein ungültiges Token. Daran ändert ein Retry nichts, es würde
                 * nur alle 5 Sekunden endlos weiterlaufen.
                 */
                if (err instanceof FatalSseError) {
                    return;
                }

                // Sonst (z.B. kurzer Netzabbruch) den Stream neu starten
                setTimeout(() => this.startSseStream(), 5000);
            },
        });

        // console.log(`SSE-Stream gestartet (employeeId ${employeeId})`);
    }

    public stopSseStream() {
        if (!this.subscription) {
            return;
        }

        this.subscription.unsubscribe();
        this.subscription = null;
        // console.log('SSE-Stream beendet');
    }
}
